import {
	DocumentWorkflowTemplateVisibleIn,
	type KycFormTemplateRes,
} from '@dealstack/shared/lib/generated';
import { CustomDetailEnum } from '@dealstack/shared/lib/generated/models/CustomDetailEnum.ts';
import type {
	KycCertificationRequirementFlags,
	KycProofRequirementFlags,
} from '@dealstack/shared/lib/common/business/kycCertification';
import type {
	NewJoinersAndAllocationsFormTemplate,
	NewJoinersAndAllocationsKycFormValues,
	NewJoinersAndAllocationsProofsStatus,
	NewJoinersAndAllocationsProofValues,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsStepsComponents/NewJoinersAndAllocationsKYC/useNewJoinersAndAllocationsKYCController';

type ProgrammeProofFlags = Partial<KycProofRequirementFlags & KycCertificationRequirementFlags>;

type JoinerProofUpdate = NewJoinersAndAllocationsProofValues['individualProofs'] &
	NewJoinersAndAllocationsProofValues['vehicleProofs'];

/**
 * Splits the programme's proof and certification flags into the joiner step's two audience groups.
 * The `false` fallbacks only show before the step's own `kycForm.reset` lands the server's values.
 */
export const toJoinerProofValues = (
	flags: ProgrammeProofFlags | null | undefined
): NewJoinersAndAllocationsProofValues => ({
	individualProofs: {
		isProofOfIdentityRequired: flags?.isProofOfIdentityRequired ?? false,
		isProofOfIdentityCertificationRequired:
			flags?.isProofOfIdentityCertificationRequired ?? false,
		isProofOfAddressRequired: flags?.isProofOfAddressRequired ?? false,
		isProofOfAddressCertificationRequired:
			flags?.isProofOfAddressCertificationRequired ?? false,
	},
	vehicleProofs: {
		isProofOfInvestmentVehicleRequired: flags?.isProofOfInvestmentVehicleRequired ?? false,
		isProofOfInvestmentVehicleCertificationRequired:
			flags?.isProofOfInvestmentVehicleCertificationRequired ?? false,
	},
});

/**
 * The proofs to write back to the programme, or null when the modal never loaded the programme's
 * own. Submit saves these to the MIP default, and the form reads all-`false` until its reset lands
 * the server's values — so writing them unloaded would stop the programme asking for any proof at
 * all, and no investor created afterwards is asked for identity or address.
 */
export const toProgrammeProofUpdate = (
	status: NewJoinersAndAllocationsProofsStatus,
	formValues: NewJoinersAndAllocationsProofValues
): JoinerProofUpdate | null =>
	status === 'loaded' ? { ...formValues.individualProofs, ...formValues.vehicleProofs } : null;

/**
 * Splits the programme's KYC form templates into the joiner step's two audience lists. Reads
 * `visibleIn` rather than the legacy CustomDetail `type`, which never carried PIV forms.
 */
export const toJoinerKycFormValues = (
	templates: KycFormTemplateRes[],
	proofFlags: ProgrammeProofFlags | null | undefined
): NewJoinersAndAllocationsKycFormValues =>
	templates.reduce<NewJoinersAndAllocationsKycFormValues>(
		(acc, template) => {
			const isVehicle = template.visibleIn.includes(
				DocumentWorkflowTemplateVisibleIn.KYC_VEHICLE
			);

			// No `local` flag: these already exist, and submit only sends the local ones.
			const templateData: NewJoinersAndAllocationsFormTemplate = {
				id: template.id,
				name: template.name,
				type: isVehicle ? CustomDetailEnum.VEHICLE : CustomDetailEnum.INDIVIDUAL,
				instruction: template.instruction,
				fileId: template.fileId,
				fileName: template.fileName,
			};

			return isVehicle
				? { ...acc, pivFormsTemplates: [...acc.pivFormsTemplates, templateData] }
				: {
						...acc,
						individualFormsTemplates: [...acc.individualFormsTemplates, templateData],
					};
		},
		{
			...toJoinerProofValues(proofFlags),
			individualFormsTemplates: [],
			pivFormsTemplates: [],
		}
	);
